import type { Group, Lesson, LessonRecord, Assignment } from '@/hooks/useDashboardData';

interface KpiCardsProps {
  groups: Group[];
  lessons: Lesson[];
  records: LessonRecord[];
  assignments: Assignment[];
  activeStudents: number;
  passiveStudents: number;
}

function getTodayStr() {
  return new Date().toISOString().slice(0, 10);
}

function getWeekStartStr() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return monday.toISOString().slice(0, 10);
}

function getWeekEndStr() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? 0 : 7);
  const sunday = new Date(d.setDate(diff));
  return sunday.toISOString().slice(0, 10);
}

interface Kpi {
  label: string;
  value: number;
  icon: string;
  tone: string;
  hint: string;
}

export default function KpiCards({ groups, lessons, records, assignments, activeStudents, passiveStudents }: KpiCardsProps) {
  const today = getTodayStr();
  const weekStart = getWeekStartStr();
  const weekEnd = getWeekEndStr();

  const kpis: Kpi[] = [
    { label: 'Bugünkü Dersler', value: lessons.filter((l) => l.date === today).length, icon: 'ri-calendar-check-line', tone: 'bg-primary-100 text-primary-700', hint: 'Bugün planlanan' },
    { label: 'Bu Hafta Planlanan Ders', value: lessons.filter((l) => l.date >= weekStart && l.date <= weekEnd).length, icon: 'ri-calendar-2-line', tone: 'bg-accent-100 text-accent-800', hint: 'Bu hafta' },
    { label: 'Aktif Öğrenci', value: activeStudents, icon: 'ri-user-star-line', tone: 'bg-emerald-100 text-emerald-700', hint: `Pasif: ${passiveStudents}` },
    { label: 'Bekleyen Ödev', value: assignments.filter((a) => a.status === 'gonderildi' || a.status === 'goruldu').length, icon: 'ri-task-line', tone: 'bg-amber-100 text-amber-700', hint: 'Kontrol bekliyor' },
    { label: 'Ders Kayıtları', value: records.length, icon: 'ri-vidicon-line', tone: 'bg-teal-100 text-teal-700', hint: 'Yüklenen kayıt' },
    { label: 'Aktif Grup', value: groups.length, icon: 'ri-team-line', tone: 'bg-secondary-100 text-secondary-700', hint: 'Toplam grup' },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
      {kpis.map((kpi) => (
        <div
          key={kpi.label}
          className="rounded-xl border border-background-200 bg-white p-4 transition hover:border-background-300"
        >
          <div className="flex items-start justify-between">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${kpi.tone}`}>
              <i className={`${kpi.icon} text-lg`} />
            </div>
          </div>
          <p className="mt-4 font-heading text-3xl font-bold text-foreground-950">{kpi.value}</p>
          <p className="mt-1 text-sm font-semibold text-foreground-800">{kpi.label}</p>
          <p className="mt-0.5 text-xs text-foreground-500">{kpi.hint}</p>
        </div>
      ))}
    </div>
  );
}